/**
 * Hotel Results Component
 * Handles display and interaction with hotel search results
 */

import { HotelResult, HotelFilters } from '../types/data-models';

export class HotelResultsComponent {
  private container: HTMLElement;
  private allHotels: HotelResult[] = [];
  private filteredHotels: HotelResult[] = [];
  private currentPage = 1;
  private itemsPerPage = 10;
  private currentSort = 'price';

  public onHotelSelect: ((hotelId: string) => void) | null = null;
  public onSortChange: ((sortBy: string) => void) | null = null;
  public onPageChange: ((page: number) => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.bindEvents();
  }

  private bindEvents(): void {
    // Sort change handler
    const sortSelect = document.getElementById('sort-by') as HTMLSelectElement;
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        this.currentSort = target.value;
        this.sortResults(this.currentSort);
        if (this.onSortChange) {
          this.onSortChange(this.currentSort);
        }
      });
    }
  }

  public displayHotels(hotels: HotelResult[]): void {
    this.allHotels = hotels;
    this.filteredHotels = [...hotels];
    this.currentPage = 1;
    this.sortResults(this.currentSort);
    this.renderHotels();
    this.renderPagination();
  }

  public applyFilters(filters: HotelFilters): void {
    this.filteredHotels = this.allHotels.filter(hotel => {
      // Price filter
      if (filters.priceRange) {
        if (hotel.price.min < filters.priceRange.min || hotel.price.min > filters.priceRange.max) {
          return false;
        }
      }

      // Rating filter
      if (filters.rating && filters.rating.length > 0) {
        if (!filters.rating.includes(Math.floor(hotel.rating))) {
          return false;
        }
      }

      // Amenities filter
      if (filters.amenities && filters.amenities.length > 0) {
        const hasRequiredAmenities = filters.amenities.every(amenity => 
          hotel.amenities.some(hotelAmenity => 
            hotelAmenity.toLowerCase().includes(amenity.toLowerCase())
          )
        );
        if (!hasRequiredAmenities) {
          return false;
        }
      }

      return true;
    });

    this.currentPage = 1;
    this.sortResults(this.currentSort);
    this.renderHotels();
    this.renderPagination();
  }

  public sortResults(sortBy: string): void {
    this.currentSort = sortBy;
    
    this.filteredHotels.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.price.min - b.price.min;
        case 'rating':
          return b.rating - a.rating;
        case 'distance':
          // Mock distance sorting - in real app would use actual distance
          return Math.random() - 0.5;
        case 'popularity':
          return b.reviewCount - a.reviewCount;
        default:
          return 0;
      }
    });

    this.renderHotels();
  }

  public goToPage(page: number): void {
    this.currentPage = page;
    this.renderHotels();
    this.renderPagination();
    
    if (this.onPageChange) {
      this.onPageChange(page);
    }

    // Scroll to top of results
    this.container.scrollIntoView({ behavior: 'smooth' });
  }

  private renderHotels(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const hotelsToShow = this.filteredHotels.slice(startIndex, endIndex);

    this.container.innerHTML = hotelsToShow.map(hotel => this.createHotelCard(hotel)).join('');

    // Bind click events for hotel selection
    this.container.querySelectorAll('.hotel-card__select').forEach((button, index) => {
      button.addEventListener('click', () => {
        const hotel = hotelsToShow[index];
        if (hotel && this.onHotelSelect) {
          this.onHotelSelect(hotel.id);
        }
      });
    });
  }

  private createHotelCard(hotel: HotelResult): string {
    const starsHtml = this.generateStarsHtml(hotel.rating);
    const amenitiesHtml = hotel.amenities.slice(0, 4).map(amenity => 
      `<span class="hotel-card__amenity">${amenity}</span>`
    ).join('');

    return `
      <article class="hotel-card" data-hotel-id="${hotel.id}">
        <div class="hotel-card__header">
          <div class="hotel-card__image">
            <div class="hotel-card__image-placeholder">
              <span class="hotel-card__image-text">Image de l'hôtel</span>
              <span class="hotel-card__image-note">${hotel.name}</span>
            </div>
          </div>
          <div class="hotel-card__info">
            <div class="hotel-card__main">
              <h3 class="hotel-card__name">${hotel.name}</h3>
              <div class="hotel-card__rating">
                <div class="hotel-card__stars">${starsHtml}</div>
                <span class="hotel-card__rating-text">${hotel.rating.toFixed(1)}</span>
                <span class="hotel-card__reviews">(${hotel.reviewCount} avis)</span>
              </div>
              <p class="hotel-card__address">${hotel.address}</p>
              ${hotel.distance ? `<p class="hotel-card__distance">${hotel.distance} du centre</p>` : ''}
            </div>
            <div class="hotel-card__price">
              <div class="hotel-card__price-amount">€${hotel.price.min}</div>
              <div class="hotel-card__price-label">par nuit</div>
              ${hotel.breakfast ? '<div class="hotel-card__breakfast">Petit-déjeuner inclus</div>' : ''}
            </div>
          </div>
        </div>
        
        <div class="hotel-card__details">
          <div class="hotel-card__description">
            <p>${hotel.description}</p>
          </div>
          
          <div class="hotel-card__amenities">
            <h4 class="hotel-card__amenities-title">Équipements</h4>
            <div class="hotel-card__amenities-list">
              ${amenitiesHtml}
              ${hotel.amenities.length > 4 ? `<span class="hotel-card__amenities-more">+${hotel.amenities.length - 4} autres</span>` : ''}
            </div>
          </div>
          
          <div class="hotel-card__policies">
            <div class="hotel-card__cancellation">
              <strong>Annulation:</strong> ${hotel.cancellation}
            </div>
          </div>
        </div>
        
        <div class="hotel-card__actions">
          <button type="button" class="btn btn-primary hotel-card__select">
            Voir les détails
          </button>
          <button type="button" class="btn btn-outline hotel-card__map" title="Voir sur la carte">
            📍 Carte
          </button>
        </div>
      </article>
    `;
  }

  private generateStarsHtml(rating: number): string {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    let starsHtml = '';
    
    // Full stars
    for (let i = 0; i < fullStars; i++) {
      starsHtml += '<span class="hotel-card__star hotel-card__star--full">★</span>';
    }
    
    // Half star
    if (hasHalfStar) {
      starsHtml += '<span class="hotel-card__star hotel-card__star--half">★</span>';
    }
    
    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      starsHtml += '<span class="hotel-card__star hotel-card__star--empty">☆</span>';
    }

    return starsHtml;
  }

  private renderPagination(): void {
    const totalPages = Math.ceil(this.filteredHotels.length / this.itemsPerPage);
    const paginationContainer = document.getElementById('pagination');
    
    if (!paginationContainer || totalPages <= 1) {
      if (paginationContainer) {
        paginationContainer.style.display = 'none';
      }
      return;
    }

    paginationContainer.style.display = 'flex';

    const prevButton = paginationContainer.querySelector('.pagination__prev') as HTMLButtonElement;
    const nextButton = paginationContainer.querySelector('.pagination__next') as HTMLButtonElement;
    const pagesContainer = paginationContainer.querySelector('.pagination__pages') as HTMLElement;

    // Update prev/next buttons
    if (prevButton) {
      prevButton.disabled = this.currentPage === 1;
      prevButton.onclick = () => {
        if (this.currentPage > 1) {
          this.goToPage(this.currentPage - 1);
        }
      };
    }

    if (nextButton) {
      nextButton.disabled = this.currentPage === totalPages;
      nextButton.onclick = () => {
        if (this.currentPage < totalPages) {
          this.goToPage(this.currentPage + 1);
        }
      };
    }

    // Generate page numbers
    if (pagesContainer) {
      pagesContainer.innerHTML = this.generatePageNumbers(totalPages);
      
      // Bind page number clicks
      pagesContainer.querySelectorAll('.pagination__page').forEach((button) => {
        button.addEventListener('click', () => {
          const pageNumber = parseInt((button as HTMLElement).dataset.page || '1');
          this.goToPage(pageNumber);
        });
      });
    }
  }

  private generatePageNumbers(totalPages: number): string {
    const pages: string[] = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(`<button class="pagination__page" data-page="1">1</button>`);
      if (startPage > 2) {
        pages.push(`<span class="pagination__ellipsis">...</span>`);
      }
    }

    // Add visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      const isActive = i === this.currentPage;
      pages.push(`
        <button class="pagination__page ${isActive ? 'pagination__page--active' : ''}" data-page="${i}">
          ${i}
        </button>
      `);
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(`<span class="pagination__ellipsis">...</span>`);
      }
      pages.push(`<button class="pagination__page" data-page="${totalPages}">${totalPages}</button>`);
    }

    return pages.join('');
  }

  public getFilteredHotelsCount(): number {
    return this.filteredHotels.length;
  }

  public getCurrentPageInfo(): { current: number; total: number } {
    return {
      current: this.currentPage,
      total: Math.ceil(this.filteredHotels.length / this.itemsPerPage)
    };
  }
}